import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import PhoneIcon from '@mui/icons-material/Phone';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';

import type { IMessage, ICreateShowingRequest } from 'src/services/api';
import { EMessageChannel } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import MessageHeader from 'src/pages/landlord/leads/messages/components/messages-container/MessageHeader';
import MessageLine from 'src/pages/landlord/leads/messages/components/messages-container/MessageLine';
import MessageSentStatusIcon from 'src/pages/landlord/leads/messages/components/messages-container/MessageSentStatusIcon';
import OpenHousePlaybookModal from 'src/pages/landlord/leads/messages/components/messages-container/OpenHousePlaybookModal';
import { parseMessageLineIntoRegions, PHONE_REGEX } from 'src/pages/landlord/leads/messages/components/messages-container/utils/parse-message-line-into-regions';
import CreateOrUpdateTourModal from 'src/components/create-or-update-tour-modal/CreateOrUpdateTourModal';
import { PlaybookUUID } from 'src/playbooks/playbook-templates';
import { useLeadMessagesPageContext } from 'src/pages/landlord/leads/messages/context/hooks';
import { useLeadMessagesMode } from 'src/pages/landlord/leads/messages/context/mode';
import { Flex, FlexColumn } from 'src/components/flex';
import convertFileSize from 'src/utils/convert-file-size';
import ContactInfoModal from 'src/pages/landlord/leads/messages/components/messages-container/ContactInfoModal';
import { prettyifyPhoneNumberWithCountryCode } from 'src/common/phone-number-utils';
import { useTranslateMessage } from 'src/services/api/messages/use-translate-message';

import styles from 'src/pages/landlord/leads/messages/components/messages-container/Message.module.css';
import propertiesAiLogoGreen from 'src/assets/img/properties/ai_logo_green.png';

dayjs.extend(relativeTime);

interface Props {
    message: IMessage,
}

/**
 * Display a single message
 */
export default function Message({
    message,
}: Props) {
    const navigate = useNavigate();

    const { loggedInUser, lead, showings, attachments } =
        useLeadMessagesPageContext();
    const { variant } = useLeadMessagesMode();
    const isDrawerVariant = variant === 'drawer';

    /**
     * When we have an existin upcoming showing, by default we will edit that showing instead of creating a new one
     */
    const upcomingShowingsForThisLead = showings.filter((showing: IShowing) => {
        return (showing.lead_uuid === lead.uuid &&
            showing.property_id === lead.property_id &&
            !showing.is_canceled &&
            showing.start_time > new Date().toISOString());
    });
    const upcomingShowingId = upcomingShowingsForThisLead.length > 0 ? upcomingShowingsForThisLead[0].id : -1;
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [showingPlaybookTarget, setShowingPlaybookTarget] = useState<ICreateShowingRequest>();
    const [contactInfoModal, setContactInfoModal] = useState<{ type: 'phone' | 'email', value: string } | null>(null);
    const [lightboxAttachmentId, setLightboxAttachmentId] = useState<number | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);

    const { mutate: translateMessage, isLoading: isTranslating } = useTranslateMessage(lead.uuid);

    const handleCloseOpenHousePlaybookModal = (goToPlaybooks: boolean) => {
        if (!showingPlaybookTarget) {
            throw new Error('showingPlaybookTarget is null');
        }
        const propertyId = showingPlaybookTarget.property_id;
        setShowingPlaybookTarget(undefined);
        if (goToPlaybooks) {
            const playbookUuid = PlaybookUUID.ManualSendEmailNotBookedShowing;
            const path = '/landlord/inner/playbooks/new';
            const url = `${path}?template_uuid=${playbookUuid}&property_id=${propertyId}`;
            navigate(url);
        }
    };

    const isSystemMessage = message.system_message_type;

    let className: string[] | undefined;
    if (isSystemMessage) {
        className = [styles.systemMessage];
    } else if (message.direction === 'out') {
        className = [styles.messageFromAgent];
    } else if (message.direction === 'in') {
        className = [styles.messageFromLead];
    }

    let iconBackgroundColor;
    let iconColor;
    if (message.direction === 'out') {
        iconBackgroundColor = '#9EFFDD';
        iconColor = '#000000';
    } else {
        iconBackgroundColor = '#10B981';
        iconColor = '#FFFFFF';
    }

    const messageLines = message.contents.split('\n').map((line: string, i: number) => {
        const regions = parseMessageLineIntoRegions(line, i);
        return (
            <div key={`line-${i}`} className={styles.messageLine}>
                <MessageLine
                    regions={regions}
                    onDateClick={(date) => setSelectedDate(dayjs(date).format('YYYY-MM-DDTHH:mm:ss'))}
                    onContactClick={(type, value) => setContactInfoModal({ type, value })}
                />
            </div>
        );
    });

    const handleCloseShowingModal = () => {
        setSelectedDate(null);
        setShowingPlaybookTarget(undefined);
    };

    const messageAttachments = attachments?.filter((attachment) => attachment.message_uuid === message.uuid) ?? [];
    const hideMessageBlock = messageAttachments.length > 0 && (
        message.channel === EMessageChannel.FACEBOOK_MESSENGER || message.contents.trim().length === 0
    );

    const isPlaceholder = message.uuid?.includes('placeholder') || false;
    const isCallSystemMessage = isSystemMessage && (
        message.system_message_type === 'missed_call' ||
        message.system_message_type === 'inbound_connected_call' ||
        message.system_message_type === 'outbound_connected_call'
    );

    const callToneClass = isCallSystemMessage ? (
        message.system_message_type === 'missed_call' ? styles.callToneMissed :
            message.system_message_type === 'outbound_connected_call' ? styles.callToneOutbound :
                styles.callToneInbound
    ) : undefined;

    const callDetailsText = isCallSystemMessage ?
        (() => {
            const match = message.contents.match(PHONE_REGEX)?.[0];
            if (!match) {
                return message.contents;
            }
            const pretty = prettyifyPhoneNumberWithCountryCode(match);
            return pretty === 'Not Set' ? match : pretty;
        })() :
        message.contents;

    return (
        <div className={clsx(styles.messageBlock, className, isPlaceholder && styles.placeholder, isTranslating && styles.translating, isDrawerVariant && styles.drawerCompact)}>
            {showingPlaybookTarget && (
                <OpenHousePlaybookModal
                    open={showingPlaybookTarget !== null}
                    onClose={handleCloseOpenHousePlaybookModal}
                />
            )}

            {selectedDate && (
                <CreateOrUpdateTourModal
                    open={selectedDate !== null}
                    onClose={handleCloseShowingModal}
                    selectedLeadUuid={lead.uuid}
                    landlordUserId={loggedInUser.id}
                    leads={[lead]}
                    initDateTime={selectedDate}
                    initPropertyId={lead.property_id}
                    showingId={upcomingShowingId === -1 ? undefined : upcomingShowingId}
                    sourceMessageUuid={message.uuid}
                    onCreated={setShowingPlaybookTarget}
                />
            )}

            {contactInfoModal && (
                <ContactInfoModal
                    open={contactInfoModal !== null}
                    onClose={() => setContactInfoModal(null)}
                    type={contactInfoModal.type}
                    value={contactInfoModal.value}
                    lead={lead}
                    loggedInUser={loggedInUser}
                />
            )}

            <FlexColumn
                alignItems={isSystemMessage ? 'center' : message.direction === 'in' ? 'start' : 'end'}
            >
                {!hideMessageBlock && <div className={clsx(styles.messageContentsOuter, isCallSystemMessage && styles.callMessageBubble)}>
                    {!isSystemMessage && <MessageHeader message={message} isPlaceholder={isPlaceholder} />}

                    <div className={styles.messageBody}>
                        {isCallSystemMessage ? (
                            <div className={styles.callMessage}>
                                <span className={clsx(styles.callIcon, callToneClass)}>
                                    {message.system_message_type === 'missed_call' && <PhoneMissedIcon fontSize="inherit" />}
                                    {message.system_message_type === 'inbound_connected_call' && <PhoneIcon fontSize="inherit" />}
                                    {message.system_message_type === 'outbound_connected_call' && <PhoneIcon fontSize="inherit" />}
                                </span>
                                <span className={clsx(styles.callType, callToneClass)}>
                                    {message.system_message_type === 'missed_call' && 'Missed Call'}
                                    {message.system_message_type === 'inbound_connected_call' && 'Inbound Call'}
                                    {message.system_message_type === 'outbound_connected_call' && 'Outbound Call'}
                                </span>
                                <span className={styles.callSeparator} aria-hidden="true">•</span>
                                <span className={styles.callDetails}>{callDetailsText}</span>
                                <span className={styles.callSeparator} aria-hidden="true">•</span>
                                <span className={styles.callTimestamp}>{dayjs(message.inserted_at).fromNow()}</span>
                            </div>
                        ) : (
                            <>
                                {message.translated_contents && (message.direction === 'in' ? !showOriginal : showOriginal) ?
                                    message.translated_contents.split('\n').map((line: string, i: number) => {
                                        const regions = parseMessageLineIntoRegions(line, i);
                                        return (
                                            <div key={`tline-${i}`} className={styles.messageLine}>
                                                <MessageLine regions={regions} />
                                            </div>
                                        );
                                    }) :
                                    messageLines}

                                {((message.direction === 'in' && !!message.lang && message.lang !== 'en') ||
                                  (message.direction === 'out' && !!message.translated_contents)) && !isSystemMessage && (
                                    <Box mt={1} className={styles.translationSection}>
                                        {message.translated_contents ? (
                                            <>
                                                <Box display="flex" alignItems="center" gap={0.5} className={styles.translationBadge}>
                                                    <img src={propertiesAiLogoGreen} alt="AI" className={styles.aiLogo} />
                                                    <Typography variant="caption" color="inherit">
                                                        {(() => {
                                                            const lang = message.lang && message.lang !== 'en' ? message.lang : null;
                                                            const langName = lang ?
                                                                (new Intl.DisplayNames(['en'], { type: 'language' }).of(lang) ?? lang) :
                                                                null;
                                                            const preposition = message.direction === 'in' ? 'from' : 'to';
                                                            return langName ?
                                                                <span>Translated by Reffie AI {preposition} <strong>{langName}</strong></span> :
                                                                'Translated by Reffie AI';
                                                        })()}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    component="button"
                                                    className={styles.translationToggle}
                                                    onClick={() => setShowOriginal(prev => !prev)}
                                                >
                                                    {showOriginal ? 'Show translated' : 'Show original'}
                                                </Box>
                                            </>
                                        ) : isTranslating ? (
                                            <Box display="flex" alignItems="center" gap={0.5} className={styles.translationPending}>
                                                <img src={propertiesAiLogoGreen} alt="AI" className={styles.aiLogo} />
                                                <Typography variant="caption" color="inherit">Translation in progress...</Typography>
                                            </Box>
                                        ) : (
                                            <Tooltip title="Translate to English with AI">
                                                <Box
                                                    component="button"
                                                    className={styles.translateButton}
                                                    onClick={() => {
                                                        if (!message.uuid) { return; }
                                                        translateMessage(message.uuid);
                                                    }}
                                                >
                                                    <img src={propertiesAiLogoGreen} alt="AI" className={styles.aiLogo} />
                                                    Translate message
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </>
                        )}
                    </div>

                    <div className={styles.messageMetadata}>
                        <div className={styles.messageStatus}>
                            {message.direction === 'out' && message.channel === EMessageChannel.SMS && (
                                <MessageSentStatusIcon message={message} />
                            )}
                        </div>
                    </div>
                </div>}

                {messageAttachments.map((attachment) => {
                    const fileExt = attachment.mimetype?.split('/')[1].toUpperCase();
                    const attType = attachment.mimetype?.split('/')[0];
                    const attUnit =
                        attachment.file_size >= 1024 * 1024 ? 'MB' : attachment.file_size >= 1024 ? 'KB' : 'bytes';
                    const attUrl = attachment.download_url;

                    if (attType === 'image') {
                        return (
                            <Fragment key={attachment.id}>
                                <div className={clsx(styles.messageContentsOuter, styles.imageBubble)}>
                                    <Box
                                        className={styles.imageContainer}
                                        onClick={() => setLightboxAttachmentId(attachment.id)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <img
                                            src={attUrl}
                                            alt={attachment.original_filename}
                                            width="240"
                                            height="240"
                                            className={styles.attachmentImage}
                                        />
                                    </Box>
                                </div>
                                <Dialog
                                    open={lightboxAttachmentId === attachment.id}
                                    onClose={() => setLightboxAttachmentId(null)}
                                    maxWidth={false}
                                    PaperProps={{
                                        sx: {
                                            backgroundColor: 'transparent',
                                            boxShadow: 'none',
                                            overflow: 'visible',
                                            position: 'relative',
                                        }
                                    }}
                                >
                                    <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1, zIndex: 1 }}>
                                        <Tooltip title={`${convertFileSize(attachment.file_size)} ${attUnit} \u2022 ${fileExt}`}>
                                            <Link href={attUrl} download>
                                                <IconButton sx={{ backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}>
                                                    <FileDownloadOutlinedIcon sx={{ color: '#fff' }} />
                                                </IconButton>
                                            </Link>
                                        </Tooltip>
                                        <IconButton
                                            onClick={() => setLightboxAttachmentId(null)}
                                            sx={{ backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                                        >
                                            <CloseIcon sx={{ color: '#fff' }} />
                                        </IconButton>
                                    </Box>
                                    <img
                                        src={attUrl}
                                        alt={attachment.original_filename}
                                        style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }}
                                    />
                                </Dialog>
                            </Fragment>
                        );
                    }

                    return (
                        <div key={attachment.id} className={styles.messageContentsOuter}>
                            <MessageHeader message={message} isPlaceholder={isPlaceholder} />
                            <Flex justifyContent="space-between" mt={1} gap={6}>
                                <Flex gap={2} minWidth={0}>
                                    <Flex alignItems="center">
                                        <Avatar sx={{ bgcolor: iconBackgroundColor }}>
                                            <InsertDriveFileIcon sx={{ fontSize: 20, color: iconColor }} />
                                        </Avatar>
                                    </Flex>
                                    <FlexColumn>
                                        <Typography variant="body2" className={clsx(styles.attachmentName, styles.messageBody)}>
                                            {attachment.original_filename}
                                        </Typography>
                                        <Tooltip title={fileExt}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    maxWidth: { xs: '80px', sm: '150px', md: '200px' },
                                                }}
                                                className={clsx('ellipsisText', styles.attachmentDetails, styles.messageBody, className)}
                                            >
                                                {convertFileSize(attachment.file_size)} {attUnit} &#x2022; {fileExt}
                                            </Typography>
                                        </Tooltip>
                                    </FlexColumn>
                                </Flex>
                                <Flex alignItems="center">
                                    <Link href={attUrl} download>
                                        <IconButton>
                                            <FileDownloadOutlinedIcon />
                                        </IconButton>
                                    </Link>
                                </Flex>
                            </Flex>
                        </div>
                    );
                })}
            </FlexColumn>
        </div>
    );
}
